import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

import { MatIconModule } from '@angular/material/icon';

import { TimelineBlock } from '@core/services/ai/ai-assistant.service';

@Component({
  selector: 'app-ai-timeline-block',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule],
  template: `
    @if (block.title) {
      <div class="tl__title">{{ block.title }}</div>
    }
    <ol class="tl">
      @for (e of block.events; track $index) {
        <li class="tl__event" [attr.data-tone]="e.tone || 'neutral'">
          <div class="tl__dot">
            <mat-icon>{{ e.icon || 'circle' }}</mat-icon>
          </div>
          <div class="tl__body">
            <div class="tl__head">
              <span class="tl__date">{{ formatDate(e.date) }}</span>
              <span class="tl__label">{{ e.label }}</span>
            </div>
            @if (e.description) {
              <div class="tl__desc">{{ e.description }}</div>
            }
          </div>
        </li>
      }
    </ol>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .tl__title {
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: rgba(15, 23, 42, 0.55);
        margin: 0 0 6px;
      }
      .tl {
        list-style: none;
        margin: 0;
        padding: 0;
        position: relative;
        background: #fff;
        border: 1px solid rgba(15, 23, 42, 0.08);
        border-radius: 12px;
        padding: 10px 12px 12px 14px;
      }
      .tl__event {
        position: relative;
        padding-left: 28px;
        padding-bottom: 12px;
      }
      .tl__event:last-child {
        padding-bottom: 0;
      }
      .tl__event::before {
        content: '';
        position: absolute;
        left: 11px;
        top: 18px;
        bottom: 0;
        width: 2px;
        background: rgba(99, 102, 241, 0.2);
      }
      .tl__event:last-child::before {
        display: none;
      }
      .tl__dot {
        position: absolute;
        left: 0;
        top: 0;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: rgba(99, 102, 241, 0.12);
        display: grid;
        place-items: center;
      }
      .tl__dot mat-icon {
        font-size: 14px;
        width: 14px;
        height: 14px;
        color: #6366f1;
      }
      .tl__event[data-tone='positive'] .tl__dot {
        background: rgba(16, 185, 129, 0.15);
      }
      .tl__event[data-tone='positive'] .tl__dot mat-icon {
        color: #10b981;
      }
      .tl__event[data-tone='warning'] .tl__dot {
        background: rgba(245, 158, 11, 0.15);
      }
      .tl__event[data-tone='warning'] .tl__dot mat-icon {
        color: #f59e0b;
      }
      .tl__event[data-tone='danger'] .tl__dot {
        background: rgba(239, 68, 68, 0.15);
      }
      .tl__event[data-tone='danger'] .tl__dot mat-icon {
        color: #ef4444;
      }
      .tl__head {
        display: flex;
        align-items: baseline;
        gap: 10px;
        flex-wrap: wrap;
      }
      .tl__date {
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: rgba(15, 23, 42, 0.5);
      }
      .tl__label {
        font-weight: 600;
        font-size: 13px;
        color: rgba(15, 23, 42, 0.92);
        overflow-wrap: anywhere;
      }
      .tl__desc {
        margin-top: 2px;
        font-size: 12px;
        color: rgba(15, 23, 42, 0.65);
        line-height: 1.4;
        overflow-wrap: anywhere;
      }
    `
  ]
})
export class AiTimelineBlockComponent {
  @Input({ required: true }) block!: TimelineBlock;

  formatDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  }
}
